import clsx from "clsx";
import { FC, useEffect, useMemo, useRef, useTransition } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  CheckboxControlledMui,
  DropdownTemplate,
  MessagesDisplayContainer,
} from "@ryde/controls";
import { normalizePhoneNumber } from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  ChooseStationStepFormFieldsEnum,
  landingColors,
  landingDropdownStationListAtom,
  landingMessagesAtom,
  profileLandingDtoAtom,
  useLandingProfile,
} from "@ryde/utils-landings";

import { IMarkerLocation, LandingAddressMap } from "../../layout/landing-map";
import { LandingAddressTemplate } from "../../layout/landing-templates";
import { ContinueButton } from "../../widgets";
import { useStepChooseStation } from "./use-step-choose-station";

export const StepChooseStation: FC = () => {
  const {
    methods,
    handleChooseStation,
    handleHomeAddressChange,
    isContinueCanBeApply,
  } = useStepChooseStation();
  const { watch, setValue } = methods;

  const landingMessages = useRecoilValue(landingMessagesAtom);
  const stationsDropdownList = useRecoilValue(landingDropdownStationListAtom);
  const isHomeAddressTouchedRef = useRef<boolean>(false);
  const { fetchAndSetProfile } = useLandingProfile();
  const [, startHomeAddressChangeTransition] = useTransition();
  const [, startFetchProfileTransition] = useTransition();
  const profileDto = useRecoilValue(profileLandingDtoAtom);
  const isApiLoading = useRecoilValue(apiDataFetchingLandingAtom);
  const { t } = useTranslation();

  const {
    latitude: lat,
    longitude: lng,
    fullAddress,
  } = watch(ChooseStationStepFormFieldsEnum.HomeAddress);
  const acceptTermsWatchField = watch(
    ChooseStationStepFormFieldsEnum.AcceptTerms,
  );
  const sourceStation2IdWatchField = watch(
    ChooseStationStepFormFieldsEnum.SourceStation2Id,
  );

  const markers = useMemo(() => {
    if (!lat || !lng) {
      return [];
    }

    const locations: IMarkerLocation[] = [
      {
        coordinate: { lat, lng },
        isCenter: true,
        popupText: fullAddress,
        icon: <SVG_MUI.HomePinSVG color={landingColors.purpleHaze} />,
      },
    ];

    const { locationInfo: pickUpLocation, label } =
      stationsDropdownList?.find(
        item => item.value === sourceStation2IdWatchField,
      ) ?? {};

    if (pickUpLocation?.latitude && pickUpLocation?.longitude) {
      locations.push({
        coordinate: {
          lat: pickUpLocation.latitude,
          lng: pickUpLocation.longitude,
        },
        isCenter: false,
        popupText: label ?? pickUpLocation.fullAddress,
      });
    }

    return locations;
  }, [fullAddress, lat, lng, sourceStation2IdWatchField, stationsDropdownList]);

  useEffect(() => {
    if ((lat || lng || fullAddress) && !isHomeAddressTouchedRef.current) {
      isHomeAddressTouchedRef.current = true;
    }

    if (isHomeAddressTouchedRef.current) {
      startHomeAddressChangeTransition(() => {
        handleHomeAddressChange();
      });
    }
  }, [lat, lng, fullAddress, handleHomeAddressChange]);

  useEffect(() => {
    startFetchProfileTransition(() => {
      fetchAndSetProfile();
    });
  }, [fetchAndSetProfile]);

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={e => e.preventDefault()}>
        <h1 className="landing-title">{t("landing.chooseStation.title")}</h1>

        <div className="address-form">
          <ul className="passenger-details-list">
            {profileDto?.firstName && (
              <li>
                <span>{t("landing.formLabel.firstName")}:</span>{" "}
                {profileDto?.firstName}
              </li>
            )}
            {profileDto?.lastName && (
              <li>
                <span>{t("landing.formLabel.lastName")}:</span>{" "}
                {profileDto?.lastName}
              </li>
            )}
            {profileDto?.unitName && (
              <li>
                <span>{t("landing.formLabel.unitAffiliation")}:</span>{" "}
                {profileDto?.unitName}
              </li>
            )}
            {profileDto?.destinationStationName && (
              <li>
                <span>{t("landing.formLabel.unitAddress")}:</span>{" "}
                {profileDto?.destinationStationName}
              </li>
            )}
            {profileDto?.phone && (
              <li>
                <span>{t("landing.formLabel.phoneMobile")}:</span>{" "}
                {normalizePhoneNumber(profileDto?.phone)}
              </li>
            )}
          </ul>

          <div className="divider-bar margin-block-30" />

          <div className="landing-form-field">
            <label htmlFor={ChooseStationStepFormFieldsEnum.HomeAddress}>
              {t("landing.formLabel.homeAddress")}*
            </label>
            <LandingAddressTemplate
              name={ChooseStationStepFormFieldsEnum.HomeAddress}
              uniqueAttrId={ChooseStationStepFormFieldsEnum.HomeAddress}
              formAttributes={{
                isCoordinate: true,
              }}
            />
          </div>

          <div
            className={clsx("landing-form-field", {
              hidden: !stationsDropdownList?.length || !fullAddress,
            })}
          >
            <label htmlFor={ChooseStationStepFormFieldsEnum.SourceStation2Id}>
              {t("landing.formLabel.sourceStation2Id")}*
            </label>
            <DropdownTemplate
              name={ChooseStationStepFormFieldsEnum.SourceStation2Id}
              uniqueAttrId={ChooseStationStepFormFieldsEnum.SourceStation2Id}
              size="medium"
              formAttributes={{
                selectItems: stationsDropdownList,
                skipDropdownItemsTranslation: true,
              }}
            />
          </div>

          <div className="landing-form-field">
            <div className="landing-address-map-field">
              <LandingAddressMap locations={markers} readonly />
            </div>
          </div>

          <div className="landing-form-field">
            <div className="landing-accept-terms">
              <CheckboxControlledMui
                width={16}
                height={16}
                checked={acceptTermsWatchField ?? false}
                setCheckedToggle={() =>
                  setValue(
                    ChooseStationStepFormFieldsEnum.AcceptTerms,
                    !acceptTermsWatchField,
                  )
                }
                e2eData="rides-accept-terms-checkbox"
                uniqueAttrId={ChooseStationStepFormFieldsEnum.AcceptTerms}
              />
              <span className="custom-label">
                {t("landing.formLabel.acceptTerms")} הנמצאים{" "}
                <a
                  href={
                    "https://land.gil.goryde.com/public/docs/justice_tos_privacy.pdf"
                  }
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t("landing.formLabel.acceptTerms")}
                >
                  כאן
                </a>
              </span>
            </div>
          </div>

          <MessagesDisplayContainer
            serverMessages={landingMessages?.messages}
            type={landingMessages?.type}
          />

          <ContinueButton
            disabled={!isContinueCanBeApply}
            onClick={handleChooseStation}
            loading={isApiLoading}
          />
        </div>

        <div className="landing-container-footer">
          <div
            className="landing-container-footer-logo"
            aria-hidden="true"
          ></div>
        </div>
      </form>
    </FormProvider>
  );
};
